import {
  ResumeSectionType,
  ContactInfo,
  WorkExperienceCompany,
  WorkExperienceRole as SharedWorkExperienceRole,
  Education,
  SkillCategory,
  Certification,
  Project,
  ProfessionalSummaryEntry,
  contactDetailLineParts,
  contactFullName,
} from '@applicator/shared'
import React from 'react'

import type { ResumeTemplateId } from '../../types/resumeTemplate'
import type { ResolvedResumeColors } from '../../types/resumeColorScheme'
import ContactDetailLine from './ContactDetailLine'
import WorkExperienceRoleTealHeader from './WorkExperienceRoleTealHeader'
import { getSelectedTargetTitle } from '../../utils/targetTitleUtils'
import {
  isWorkExperienceCompanyDescriptionInPreview,
  isWorkExperienceCompanyHeaderInRolePreview,
  isWorkExperienceCompanyInPreview,
  isWorkExperienceRoleInPreview,
} from '../../utils/workExperiencePreview'
import { PROJECTS_SECTION_IN_PREVIEW } from '../../utils/resumePreviewFlags'
import {
  formatCertificationDates,
  isCertificationCredentialLinkInPreview,
  isCertificationDatesInPreview,
  isCertificationInPreview,
  isCertificationIssuerInPreview,
  isCertificationNameInPreview,
} from '../../utils/certificationPreview'
import { workExperienceBlockId } from '../../lib/optimization/blockIds'
import type { ResumePreviewColumnSlice } from './ResumePreviewColumnSliceContext'
import PaysafeTwoColumnPreview from './templates/PaysafeTwoColumnPreview'

const TEXT_BLACK_CLASS = 'text-black'
const TEXT_GRAY_400_CLASS = 'text-text-muted'

interface WorkExperienceRole {
  id: string
  position?: string
  positionIncluded?: boolean
  employmentType?: string
  employmentTypeIncluded?: boolean
  location?: string
  locationIncluded?: boolean
  startDate?: string
  endDate?: string
  bulletPoints?: Array<{ id: string; text: string; included: boolean }>
  included?: boolean
}

function SectionHeader({ title, accentHex }: { title: string; accentHex: string }) {
  return (
    <div className="mb-4">
      <h2
        className="text-sm font-normal uppercase mb-1 resume-section-header"
        style={{ color: accentHex }}
      >
        {title}
      </h2>
      <div className="border-b border-black" style={{ height: '1px' }} />
    </div>
  )
}

export default function ResumePreview({ 
  data,
  template = 'classic',
  colors,
  columnSlice = 'full',
  onTargetTitleClick: _onTargetTitleClick,
  onWorkExperienceRoleClick,
  onWorkExperienceBulletClick,
  onTargetTitleItemClick,
  onProfessionalSummaryClick,
  onEducationClick,
  onSkillCategoryClick,
  onCertificationClick,
  onProjectClick,
}: { 
  data: Record<ResumeSectionType, Record<string, unknown>>
  template?: ResumeTemplateId
  colors: ResolvedResumeColors
  columnSlice?: ResumePreviewColumnSlice
  onTargetTitleClick?: () => void
  onWorkExperienceRoleClick?: (roleId: string) => void
  onWorkExperienceBulletClick?: (roleId: string, bulletId: string) => void
  onTargetTitleItemClick?: (title: string) => void
  onProfessionalSummaryClick?: (summaryId: string) => void
  onEducationClick?: (educationId: string) => void
  onSkillCategoryClick?: (categoryId: string) => void
  onCertificationClick?: (certId: string) => void
  onProjectClick?: (projectId: string) => void
}) {
  if (template === 'two_column') {
    return (
      <PaysafeTwoColumnPreview
        data={data}
        colors={colors}
        columnSlice={columnSlice}
        onWorkExperienceRoleClick={onWorkExperienceRoleClick}
        onWorkExperienceBulletClick={onWorkExperienceBulletClick}
        onTargetTitleItemClick={onTargetTitleItemClick}
        onProfessionalSummaryClick={onProfessionalSummaryClick}
        onEducationClick={onEducationClick}
        onSkillCategoryClick={onSkillCategoryClick}
        onCertificationClick={onCertificationClick}
        onProjectClick={onProjectClick}
      />
    )
  }

  const contactInfo = data.contact_info as ContactInfo
  const fullName = contactFullName(contactInfo) || 'Your Name'
  const contactDetailParts = contactDetailLineParts(contactInfo)

  return (
    <div
      className="w-full mx-auto bg-surface-default"
      data-resume-template={template}
      style={
        {
          '--resume-accent-color': colors.accentHex,
          '--resume-name-color': colors.nameHex,
          '--resume-section-color': colors.sectionHeaderHex,
        } as React.CSSProperties
      }
    >
      {/* Name and Contact */}
      <div data-preview-section="contact_info">
        <h1
          className={`text-3xl font-bold mb-2 text-center template-${template}`}
          style={{ color: colors.nameHex }}
        >
          {fullName}
        </h1>
        {contactDetailParts.length > 0 && (
          <>
            <ContactDetailLine
              parts={contactDetailParts}
              className={`text-xs ${TEXT_BLACK_CLASS} mb-2`}
            />
            <div className="border-b border-black"></div>
          </>
        )}
      </div>

      {/* Target Title — only the selected entry (index 0) appears on the resume */}
      {getSelectedTargetTitle(data.target_title) && (
        <div className="mt-4 mb-6" data-preview-section="target_title">
          {(() => {
            const title = getSelectedTargetTitle(data.target_title)!
            return (
            <h2
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
              role="button"
              tabIndex={0}
              onClick={() => onTargetTitleItemClick?.(title)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && onTargetTitleItemClick) {
                  e.preventDefault()
                  onTargetTitleItemClick(title)
                }
              }}
              className={`text-base font-semibold ${TEXT_BLACK_CLASS} mb-2 cursor-pointer hover:opacity-80 transition-opacity`}
            >
              {title}
            </h2>
            )
          })()}
        </div>
      )}

      {/* Professional Summary */}
      {data.professional_summary && Array.isArray(data.professional_summary) && data.professional_summary.length > 0 && (
        <div className={getSelectedTargetTitle(data.target_title) ? 'mb-6' : 'mt-4 mb-6'} data-preview-section="professional_summary">
          {getSelectedTargetTitle(data.target_title) ? null : (
            <h2 className={`text-base font-semibold ${TEXT_BLACK_CLASS} mb-2`}>
              Chief Product Officer (CPO) / Head of Product
            </h2>
          )}
          {data.professional_summary
            .filter((entry: ProfessionalSummaryEntry) => entry.included)
            .map((entry: ProfessionalSummaryEntry, idx: number) => (
              <p 
                key={entry.id || idx} 
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                role="button"
                tabIndex={0}
                className={`text-sm font-normal ${TEXT_BLACK_CLASS} leading-relaxed mb-3 whitespace-pre-wrap cursor-pointer hover:opacity-80 transition-opacity`}
                onClick={() => {
                  if (onProfessionalSummaryClick) {
                    onProfessionalSummaryClick(entry.id)
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && onProfessionalSummaryClick) {
                    e.preventDefault()
                    onProfessionalSummaryClick(entry.id)
                  }
                }}
              >
                {entry.text}
              </p>
            ))}
        </div>
      )}

      {/* Work Experience */}
      {data.work_experience && Array.isArray(data.work_experience) && data.work_experience.length > 0 && (
        <div className="mb-6" data-preview-section="work_experience">
          <SectionHeader title="WORK EXPERIENCE" accentHex={colors.sectionHeaderHex} />
            {data.work_experience
              .filter((company: WorkExperienceCompany) => isWorkExperienceCompanyInPreview(company))
              .map((company: WorkExperienceCompany, companyIndex: number) => (
                <div
                  key={`${company.id}-${companyIndex}`}
                  className="mb-6"
                  data-preview-section={`work_experience:${workExperienceBlockId(company.name)}`}
                >
                  {(company.roles || [])
                    .filter((role: WorkExperienceRole) =>
                      isWorkExperienceRoleInPreview(company, role as SharedWorkExperienceRole)
                    )
                    .map((role: WorkExperienceRole, roleIndex: number) => (
                      <div
                        key={`${role.id}-${roleIndex}`}
                        role="button"
                        tabIndex={0}
                        className="mb-4 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          if (onWorkExperienceRoleClick) {
                            onWorkExperienceRoleClick(role.id)
                          }
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && onWorkExperienceRoleClick) {
                            e.preventDefault()
                            onWorkExperienceRoleClick(role.id)
                          }
                        }}
                      >
                        {template === 'two_column' ? (
                          <>
                            <WorkExperienceRoleTealHeader
                              company={company}
                              role={role as SharedWorkExperienceRole}
                              textClassName={TEXT_BLACK_CLASS}
                              lines="company"
                            />
                            {isWorkExperienceCompanyDescriptionInPreview(
                              company,
                              role as SharedWorkExperienceRole
                            ) && (
                              <p
                                className={`font-normal mb-2 leading-relaxed whitespace-pre-wrap ${TEXT_BLACK_CLASS}`}
                              >
                                {company.description}
                              </p>
                            )}
                            <WorkExperienceRoleTealHeader
                              company={company}
                              role={role as SharedWorkExperienceRole}
                              textClassName={TEXT_BLACK_CLASS}
                              lines="position"
                            />
                          </>
                        ) : (
                          <>
                            <WorkExperienceRoleTealHeader
                              company={company}
                              role={role as SharedWorkExperienceRole}
                              textClassName={TEXT_BLACK_CLASS}
                              lines="position"
                            />
                            <WorkExperienceRoleTealHeader
                              company={company}
                              role={role as SharedWorkExperienceRole}
                              textClassName={TEXT_BLACK_CLASS}
                              lines="company"
                            />
                          </>
                        )}
                        {template !== 'two_column' &&
                          isWorkExperienceCompanyHeaderInRolePreview(
                            company,
                            role as SharedWorkExperienceRole
                          ) &&
                          (company.descriptionIncluded ?? true) &&
                          company.description && (
                            <p
                              className={`text-sm font-normal mb-2 leading-relaxed whitespace-pre-wrap ${(role.positionIncluded ?? true) ? TEXT_BLACK_CLASS : TEXT_GRAY_400_CLASS}`}
                            >
                              {company.description}
                            </p>
                          )}
                        {/* Bullet Points - significantly indented */}
                        {(role.bulletPoints || []).filter((bp) => bp.included).length > 0 && (
                          <ul className="text-sm font-normal text-black space-y-1 ml-8 list-none">
                            {(role.bulletPoints || [])
                              .filter((bp) => bp.included)
                              .map((bp, bpIndex) => (
                                <li
                                  key={`${bp.id}-${bpIndex}`}
                                  role="button"
                                  tabIndex={0}
                                  className="paysafe-preview-bullet flex items-start cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onWorkExperienceBulletClick?.(role.id, bp.id)
                                  }}
                                  onKeyDown={(e) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && onWorkExperienceBulletClick) {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      onWorkExperienceBulletClick(role.id, bp.id)
                                    }
                                  }}
                                >
                                  <span className="mr-2 flex-shrink-0">•</span>
                                  <span className="paysafe-preview-bullet-text flex-1 leading-relaxed whitespace-pre-wrap">
                                    {bp.text}
                                  </span>
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>
              ))}
        </div>
      )}

      {/* Education */}
      {data.education && Array.isArray(data.education) && data.education.length > 0 && (
        <div className="mb-6" data-preview-section="education">
          <SectionHeader title="EDUCATION" accentHex={colors.sectionHeaderHex} />
            {data.education
              .filter((edu: Education) => edu.included ?? false)
              .map((edu: Education, idx: number) => (
                <div 
                  key={edu.id || idx} 
                  role="button"
                  tabIndex={0}
                  className="mb-4 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    if (onEducationClick && edu.id) {
                      onEducationClick(edu.id)
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && onEducationClick && edu.id) {
                      e.preventDefault()
                      onEducationClick(edu.id)
                    }
                  }}
                >
                  {/* Degree and Field of Study on first line - bold, larger font */}
                  <div className="mb-1">
                    {(edu.degreeIncluded ?? false) && edu.degree && (
                      <div className={`font-bold ${TEXT_BLACK_CLASS}`} style={{ fontSize: '16px' }}>
                        {edu.degree}
                        {edu.fieldOfStudy && (
                          <span className="font-bold"> degree in {edu.fieldOfStudy}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Institution and Dates on same line - dates right-aligned */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`text-sm font-bold ${TEXT_BLACK_CLASS}`}>{edu.institution}</span>
                      {(edu.locationIncluded ?? false) && edu.location && (
                        <span className={`text-sm font-normal ${TEXT_BLACK_CLASS}`}> • {edu.location}</span>
                      )}
                    </div>
                    {((edu.startDateIncluded ?? false) && edu.startDate) || ((edu.endDateIncluded ?? false) && edu.endDate) ? (
                      <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} text-right ml-4 whitespace-nowrap`}>
                        {(edu.startDateIncluded ?? false) && edu.startDate ? edu.startDate : ''} 
                        {(edu.startDateIncluded ?? false) && edu.startDate && (edu.endDateIncluded ?? false) && edu.endDate ? ' - ' : ''} 
                        {(edu.endDateIncluded ?? false) && edu.endDate ? edu.endDate : ''}
                      </div>
                    ) : null}
                  </div>
                  {/* Additional Information */}
                  {(edu.additionalInformationIncluded ?? false) && edu.additionalInformation && (
                    <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} mb-2 mt-1 leading-relaxed whitespace-pre-wrap`}>{edu.additionalInformation}</div>
                  )}
                </div>
              ))}
        </div>
      )}

      {/* Skills */}
      {data.skills && Array.isArray(data.skills) && data.skills.length > 0 && (
        <div className="mb-6" data-preview-section="skills">
          <SectionHeader title="SKILLS" accentHex={colors.sectionHeaderHex} />
          <div className={template === 'two_column' ? 'columns-3 gap-x-6' : undefined}>
            {data.skills.map((category: SkillCategory) => (
              <div 
                key={category.id} 
                role="button"
                tabIndex={0}
                className={`mb-3 cursor-pointer hover:opacity-80 transition-opacity ${template === 'two_column' ? 'break-inside-avoid' : ''}`}
                onClick={() => {
                  if (onSkillCategoryClick && category.id) {
                    onSkillCategoryClick(category.id)
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && onSkillCategoryClick && category.id) {
                    e.preventDefault()
                    onSkillCategoryClick(category.id)
                  }
                }}
              >
                <h3 className={`text-sm font-bold ${TEXT_BLACK_CLASS} mb-1`}>{category.name}:</h3>
                <div className={`text-sm font-normal ${TEXT_BLACK_CLASS}`}>
                  {category.skills
                    .filter((skill) => skill.included)
                    .map((skill, skillIdx) => (
                      <span key={skillIdx}>
                        {skill.name}
                        {skillIdx < category.skills.filter((s) => s.included).length - 1 && ', '}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interests */}
      {data.interests && Array.isArray(data.interests) && data.interests.length > 0 && (
        <div className="mb-6" data-preview-section="interests">
          <SectionHeader title="INTERESTS" accentHex={colors.sectionHeaderHex} />
          <div className={`text-sm font-normal ${TEXT_BLACK_CLASS}`}>
            {data.interests
              .filter((interest: { included?: boolean }) => interest.included !== false)
              .map((interest: { id?: string; name: string; included?: boolean }, idx: number) => (
                <span key={interest.id || idx}>
                  {interest.name}
                  {idx < data.interests.filter((i: { included?: boolean }) => i.included !== false).length - 1 && ', '}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Certifications */}
      {data.certifications && Array.isArray(data.certifications) && data.certifications.length > 0 && (
        <div className="mb-6" data-preview-section="certifications">
          <SectionHeader title="CERTIFICATIONS" accentHex={colors.sectionHeaderHex} />
            {data.certifications
              .filter((cert: Certification) => isCertificationInPreview(cert))
              .map((cert: Certification, idx: number) => (
                <div 
                  key={cert.id || idx} 
                  role="button"
                  tabIndex={0}
                  className="mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    if (onCertificationClick && cert.id) {
                      onCertificationClick(cert.id)
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && onCertificationClick && cert.id) {
                      e.preventDefault()
                      onCertificationClick(cert.id)
                    }
                  }}
                >
                  {(isCertificationNameInPreview(cert) || isCertificationDatesInPreview(cert)) && (
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1">
                        {isCertificationNameInPreview(cert) &&
                          (isCertificationCredentialLinkInPreview(cert) ? (
                            <a
                              href={cert.credentialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className={`text-sm font-bold ${TEXT_BLACK_CLASS} hover:text-interactive underline`}
                            >
                              {cert.name}
                            </a>
                          ) : (
                            <h3 className={`text-sm font-bold ${TEXT_BLACK_CLASS}`}>{cert.name}</h3>
                          ))}
                      </div>
                      {isCertificationDatesInPreview(cert) && (
                        <div
                          className={`text-sm font-normal ${TEXT_BLACK_CLASS} text-right ml-4 whitespace-nowrap`}
                        >
                          {formatCertificationDates(cert)}
                        </div>
                      )}
                    </div>
                  )}
                  {isCertificationIssuerInPreview(cert) && (
                    <div className="text-sm font-normal text-black">{cert.issuer}</div>
                  )}
                </div>
              ))}
        </div>
      )}

      {/* Projects — hidden from preview until layout is ready (editor still active) */}
      {PROJECTS_SECTION_IN_PREVIEW &&
        data.projects &&
        Array.isArray(data.projects) &&
        data.projects.length > 0 && (
        <div className="mb-6" data-preview-section="projects">
          <SectionHeader title="PROJECTS" accentHex={colors.sectionHeaderHex} />
            {data.projects
              .filter((project: Project) => project.included)
              .map((project: Project, idx: number) => (
                <div 
                  key={project.id || idx} 
                  role="button"
                  tabIndex={0}
                  className="mb-4 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    if (onProjectClick && project.id) {
                      onProjectClick(project.id)
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && onProjectClick && project.id) {
                      e.preventDefault()
                      onProjectClick(project.id)
                    }
                  }}
                >
                  {/* Project name and dates on same line - dates right-aligned */}
                  <div className="flex items-start justify-between mb-1">
                    <h3 className={`text-sm font-bold ${TEXT_BLACK_CLASS} flex-1`}>{project.name}</h3>
                    {project.startDate && project.endDate && (
                      <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} text-right ml-4 whitespace-nowrap`}>
                        {project.startDate} - {project.endDate}
                      </div>
                    )}
                  </div>
                  {/* Company - left aligned, no indent */}
                  {project.company && <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} mb-2`}>{project.company}</div>}
                  {/* Bullet Points - significantly indented */}
                  {project.bulletPoints && project.bulletPoints.filter((bp) => bp.included).length > 0 && (
                    <ul className={`text-sm font-normal ${TEXT_BLACK_CLASS} space-y-1 ml-8 list-none`}>
                      {project.bulletPoints
                        .filter((bp) => bp.included)
                        .map((bp, bpIdx) => (
                          <li key={bpIdx} className="paysafe-preview-bullet flex items-start">
                            <span className="mr-2 flex-shrink-0">•</span>
                            <span className="paysafe-preview-bullet-text flex-1 leading-relaxed whitespace-pre-wrap">
                              {bp.text}
                            </span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              ))}
        </div>
      )}
    </div>
  )
}
